import { LocalStorageKeys } from "../../constants";

const extensionId: string | undefined = process.env.REACT_APP_EXTENSION_ID;

export type TFetchExtensionResponse = {
  error?: string;
  status?: number;
  html?: string;
  json?: any;
  noInstall?: boolean;
};

export type TFetchExtensionParam = {
  source: string;
  config?: object;
};

export async function checkExtensionInstalled() {
  try {
    await fetch(
      `chrome-extension://${process.env.REACT_APP_EXTENSION_ID}/manifest.json`,
    );
    return true; // Fetch successful, extension is installed
  } catch (e) {
    if (e instanceof TypeError && e.message === "Failed to fetch") {
      return false; // Fetch failed, likely not installed
    } else {
      throw e; // Other error
    }
  }
}

export const fetchExtensionExternal = async ({
  source,
  config = {},
}: TFetchExtensionParam): Promise<TFetchExtensionResponse | null> => {
  if (extensionId) {
    return new Promise<TFetchExtensionResponse | null>((resolve) => {
      const handler = (result: TFetchExtensionResponse | null) => {
        if (chrome.runtime.lastError) {
          resolve({
            noInstall: true,
          });
        } else if (result) {
          resolve(result);
        } else {
          resolve({
            noInstall: true,
          });
        }
      };
      if (typeof chrome !== "undefined" && chrome.runtime) {
        chrome.runtime.sendMessage(
          extensionId,
          { type: "fetch", source, config },
          handler,
        );
      } else {
        resolve({
          noInstall: true,
        });
      }
    }).catch((e) => {
      console.error(e);
      return null;
    });
  } else {
    console.error("Extension id is not configured");
    return null;
  }
};

export const fetchExtensionInternal = async ({
  source,
  config = {},
}: TFetchExtensionParam): Promise<TFetchExtensionResponse | null> => {
  return new Promise<TFetchExtensionResponse | null>((resolve) => {
    const handler = (result: TFetchExtensionResponse | null) => {
      resolve(result);
    };
    if (typeof chrome !== "undefined" && chrome.runtime) {
      chrome.runtime.sendMessage({ type: "fetch", source, config }, handler);
    } else {
      resolve(null);
    }
  }).catch((e) => {
    console.error(e);
    return null;
  });
};

export const syncExtensionAuth = (
  shouldClose: boolean,
  onboardingState?: string | null,
  subscriptionState?: string | null,
) => {
  const accessToken = localStorage.getItem(LocalStorageKeys.ACCESS_TOKEN);
  const refreshToken = localStorage.getItem(LocalStorageKeys.REFRESH_TOKEN);
  if (typeof chrome !== "undefined" && chrome.runtime && extensionId) {
    chrome.runtime
      .sendMessage(extensionId, {
        type: "sync_auth",
        accessToken,
        refreshToken,
        onboardingState,
        subscriptionState,
        shouldClose,
      })
      .catch((e) => {
        console.error(e);
        return null;
      });
  }
};

export const logoutExtension = (shouldClose: boolean) => {
  if (typeof chrome !== "undefined" && chrome.runtime && extensionId) {
    chrome.runtime
      .sendMessage(extensionId, {
        type: "logout",
        shouldClose,
      })
      .catch((e) => {
        console.error(e);
        return null;
      });
  }
};

export const isChromeExtension = () => {
  return (
    typeof chrome !== "undefined" &&
    typeof chrome.runtime !== "undefined" &&
    typeof chrome.runtime.id !== "undefined"
  );
};
